import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { OnboardingHeader } from '../../components/OnboardingHeader';
import { ProActionButtons } from '../../components/ProActionButtons';
import { ProFooter } from '../../components/ProFooter';
import { getMe, updateMe, uploadFile } from '../../../lib/professional-api';
import { apiErrorMessage } from '../../../lib/api';
import { toast } from '../../../lib/toast';

type PortfolioImage = {
  url: string;
  caption?: string | null;
  // Presigned-read URL for rendering. The backend supplies this on load
  // (the canonical `url` points at a private bucket and won't load in an
  // <img>). For fresh uploads we stash a local blob object URL here so
  // the thumbnail shows immediately, before the next save/reload swaps in
  // a server-signed URL.
  preview_url?: string | null;
};

export default function ProPortfolioPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [images, setImages] = useState<PortfolioImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Pull the pro's saved portfolio on mount so the gallery reflects
  // what's actually persisted, not a hardcoded mock.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getMe();
        if (cancelled) return;
        const list: PortfolioImage[] = Array.isArray(
          res?.info?.portfolio_images,
        )
          ? res.info.portfolio_images
          : [];
        setImages(list);
      } catch (err) {
        if (!cancelled) {
          toast.error(apiErrorMessage(err, 'Could not load your portfolio.'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    // Snapshot the selection BEFORE clearing the input — resetting
    // `value` empties the live FileList in some browsers.
    const files = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = ''; // allow re-uploading the same filename
    if (files.length === 0) return;

    // Validate up-front so an unsupported file (e.g. an iPhone HEIC
    // photo, which the picker happily offers but S3/<img> can't use) or
    // an oversized one gives a clear reason instead of silently doing
    // nothing. Must mirror the backend's PROFESSIONAL_PORTFOLIO rule.
    const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
    const MAX_BYTES = 10 * 1024 * 1024;
    const valid: File[] = [];
    for (const file of files) {
      const isAllowedType =
        ALLOWED.includes(file.type) ||
        /\.(jpe?g|png|webp)$/i.test(file.name); // some browsers omit type
      if (!isAllowedType) {
        toast.error(
          `"${file.name}" isn't a supported image. Please upload a JPG, PNG or WebP (HEIC photos from iPhone aren't supported — change your camera format to "Most Compatible", or convert the photo first).`,
        );
        continue;
      }
      if (file.size > MAX_BYTES) {
        toast.error(
          `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is 10MB. Please resize it and try again.`,
        );
        continue;
      }
      valid.push(file);
    }
    if (valid.length === 0) return;

    setUploading(true);
    try {
      const uploaded: PortfolioImage[] = [];
      for (const file of valid) {
        const res = await uploadFile(file, 'PROFESSIONAL_PORTFOLIO');
        // `res.url` is the canonical (private) URL — not renderable on its
        // own. Use a local blob URL for the thumbnail until the next load
        // returns a server-signed `preview_url`.
        uploaded.push({
          url: res.url,
          caption: null,
          preview_url: URL.createObjectURL(file),
        });
      }
      setImages((prev) => [...prev, ...uploaded]);
      if (uploaded.length > 0) {
        toast.success(
          `${uploaded.length} photo${uploaded.length === 1 ? '' : 's'} uploaded — tap "Save portfolio" to keep ${uploaded.length === 1 ? 'it' : 'them'}.`,
        );
      }
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  const triggerUpload = () => {
    fileInputRef.current?.click();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMe({ portfolio_images: images });
      toast.success('Portfolio saved.');
      navigate('/professional/public-profile');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not save your portfolio.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <OnboardingHeader />

      <main className="flex-1 max-w-[750px] mx-auto w-full px-4 py-6 md:py-12">
        <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[24px] md:text-[34px] text-center mb-8">
          Portfolio
        </h1>

        <div className="border border-dashed border-[#eee] rounded-[16px] p-6 md:p-6 mb-12">
          {loading ? (
            <p className="text-center text-sm text-[#908e8e] py-8">
              Loading your portfolio…
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-10">
              {images.length === 0 ? (
                <p className="col-span-2 sm:col-span-4 text-center text-sm text-[#908e8e] py-8">
                  No images yet. Upload a few examples of your work.
                </p>
              ) : (
                images.map((img, i) => (
                  <div
                    key={`${img.url}-${i}`}
                    className="relative aspect-square rounded-[12px] overflow-hidden group border border-[#f0f0f0]"
                  >
                    <img
                      src={img.preview_url ?? img.url}
                      alt={img.caption ?? `Portfolio ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage(i)}
                      className="absolute top-2 right-2 transition-opacity hover:opacity-80"
                      aria-label="Remove image"
                    >
                      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="40" height="40" rx="10" fill="white" />
                        <rect width="40" height="40" rx="10" fill="#3A3335" fillOpacity="0.04" />
                        <path d="M25.2383 15.8164L21.0547 20L25.2383 24.1836L24.1836 25.2383L20 21.0547L15.8164 25.2383L14.7617 24.1836L18.9453 20L14.7617 15.8164L15.8164 14.7617L20 18.9453L24.1836 14.7617L25.2383 15.8164Z" fill="#908E8E" />
                      </svg>
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="flex justify-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              multiple
              // Specific types (not image/*) so iOS offers / converts to
              // JPEG instead of handing us an unusable HEIC.
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
            />
            <button
              onClick={triggerUpload}
              disabled={uploading || loading}
              className="bg-[#848a7c] hover:bg-[#747a6c] text-white px-8 py-3 rounded-[8px] flex items-center gap-2 font-bold transition-colors shadow-sm disabled:opacity-60"
            >
              <span className="material-icons text-[20px]">cloud_upload</span>
              {uploading ? 'Uploading…' : 'Upload photos'}
            </button>
          </div>
        </div>

        <div className="flex justify-end mb-8">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading || uploading}
            className="bg-[#8e2a2f] hover:bg-[#7a2428] text-white px-6 h-[44px] rounded-[8px] font-bold text-[14px] transition-colors disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save portfolio'}
          </button>
        </div>

        <ProActionButtons className="mt-8" />
      </main>

      <ProFooter />
    </div>
  );
}
